import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  ButtonGroup,
  Nav,
  Tab,
} from "react-bootstrap";
import api from "../api/api";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import swal from "sweetalert";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import {
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
} from "@mui/material";
import jwtDecode from "jwt-decode";
import { Select, MenuItem } from "@mui/material";
import { Cpu, MapPinned, Settings2 } from "lucide-react";
import "./SensorSettings.css";
import { getSensorDepths } from "../utils/sensorDepths";

const SensorSettings = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Existing sensor states
  const [settingValues, setSettingValues] = useState([]);
  const [frequency, setFrequency] = useState("");
  const [dateSetting, setDateSetting] = useState([]);
  const [minSetting, setMinSetting] = useState({});
  const [maxSetting, setMaxSetting] = useState({});
  const [simSettings, setSimSettings] = useState({
    simNumber: "",
    simIdentify: "",
  });
  const [formValid, setFormValid] = useState(false);
  const [resultScan, setResultScan] = useState("");
  const [sensorCoords, setSensorCoords] = useState({
    lat: "",
    lon: "",
    sensorName: "",
    locationInfo: {},
  });
  const [mv1Depth, setMv1Depth] = useState("");
  const [mv2Depth, setMv2Depth] = useState("");
  const [mv3Depth, setMv3Depth] = useState("");
  const [frequencyMultiplier, setFrequencyMultiplier] = useState("1");
  const [isAdmin, setIsAdmin] = useState(false);
  const [sensorData, setSensorData] = useState([]);
  const [sensorId, setSensorId] = useState(null);

  // NEW: Field-Sensor View states
  const [activeTab, setActiveTab] = useState("sensor"); // "sensor" or "field"
  const [fieldsData, setFieldsData] = useState([]);

  const fetchDataSensors = () => {
    api
      .get(`/sensor/sensors`)
      .then((response) => {
        let sensorData = response.data;
        setSensorData(sensorData);
      })
      .catch((error) => {
        console.error(error);
      });
  };
  const [depthSetting, setDepthSetting] = useState({});

  // NEW: Fetch fields for connected user
  const fetchFieldsByUser = () => {
    api
      .get(`/field/fields`)
      .then((response) => {
        const farms = response.data.farms || [];
        const allFields = farms.flatMap((farm) =>
          (farm.fields || []).map((field) => ({
            ...field,
            farmName: farm.name,
          }))
        );
        setFieldsData(allFields);
      })
      .catch((error) => {
        console.error(error);
        swal({
          title: t("error"),
          text: t("error_fetching_fields", { defaultValue: "Unable to load fields." }),
          icon: "error",
        });
      });
  };

  const getSingleSensor = async (id) => {
    if (id) {
      await api
        .get(`/sensor/single-sensor/${id}`)
        .then((res) => {
          let SensorData = res.data.sensor;
          let SensorLocation = res.data.location;
          setResultScan(SensorData.code);
          setSensorCoords({
            lat: SensorData.Latitude,
            lon: SensorData.Longitude,
            locationInfo: SensorLocation,
            sensorName: SensorData.sensorName || "",
          });
          setSensorId(id);
          const sensorDepths = getSensorDepths(SensorData);
          setMv1Depth(String(sensorDepths.mv1));
          setMv2Depth(String(sensorDepths.mv2));
          setMv3Depth(String(sensorDepths.mv3));
          let dataMapping = SensorData.dataMapping;
          if (dataMapping.length > 0) {
            setSettingValues(dataMapping);
            dataMapping.map((data) => {
              let frequency = data.frequency;
              setFrequency(frequency);
              setSimSettings({
                simIdentify: data.simIdentify,
                simNumber: data.simNumber,
              });
              setDateSetting(data.date);
              setMinSetting(data.min);
              setMaxSetting(data.max);
              setDepthSetting(data.depth || {});
            });
          }

        })
        .catch((error) => {
          console.error(error);
          swal({
            title: t("Error"),
            icon: "error",
          });
        });
    }
  };

  useEffect(() => {
    fetchDataSensors();
    fetchFieldsByUser();
  }, []);

  useEffect(() => {
    getSingleSensor();
  }, [sensorId]);

  const validateForm = () => {
    const isCodeValid = resultScan.trim() !== "";
    const isFrequencyValid = frequency.trim() !== "";
    setFormValid(isCodeValid && isFrequencyValid);
  };

  useEffect(() => {
    validateForm();
  }, [resultScan, frequency, dateSetting]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        setIsAdmin(decodedToken.role === "ROLE_ADMIN");
      } catch (err) {
        console.error("Invalid token", err);
      }
    }
  }, []);

  const getCalibrationValidationError = () => {
    for (let index = 0; index < 3; index++) {
      const level = `MV${index + 1}`;
      const rawMin = minSetting?.[`Mv${index + 1}_min`];
      const rawMax = maxSetting?.[`Mv${index + 1}_max`];
      const date = dateSetting?.[`Mv${index + 1}_date`];
      const hasCalibrationValue = [date, rawMin, rawMax].some(
        (value) => value !== undefined && value !== null && String(value).trim() !== ""
      );
      if (!hasCalibrationValue) continue;

      const min = Number(rawMin);
      const max = Number(rawMax);

      if (!date || !Number.isFinite(min) || !Number.isFinite(max)) {
        return t("calibration_missing_values", {
          defaultValue: `${level} : renseignez une date, un minimum brut et un maximum brut.`,
          level,
        });
      }
      if (min <= max) {
        return t("calibration_min_max_order", {
          defaultValue: `${level} : le minimum brut du sol sec doit être supérieur au maximum brut du sol humide.`,
          level,
        });
      }
    }
    return null;
  };

  const editSensor = () => {
    if (sensorId) {
      const calibrationError = getCalibrationValidationError();
      if (calibrationError) {
        swal({
          icon: "error",
          title: t("calibration_invalid_title", { defaultValue: "Étalonnage invalide" }),
          text: calibrationError,
        });
        return;
      }

      let data = {
        sensor_id: sensorId,
        code: resultScan,
        dataMapping: settingValues,
        frequency: frequency,
        lat: sensorCoords.lat,
        lon: sensorCoords.lon,
        sensorName: sensorCoords.sensorName,
        mv1: mv1Depth, // ADD THIS
        mv2: mv2Depth, // ADD THIS
        mv3: mv3Depth, // ADD THIS
      };
      if (formValid) {
        api
          .post("/sensor/edit-mapping", data)
          .then((response) => {
            if (response.data.type === "success") {
              swal(`${t("sensor_added")}`, { icon: "success" });
              setSensorData((prevSensors) =>
                prevSensors.map((s) =>
                  s.id === sensorId
                    ? {
                        ...s,
                        sensorName: sensorCoords.sensorName,
                        code: resultScan,
                      }
                    : s
                )
              );
              setSensorId(null);
              window.dispatchEvent(
                new CustomEvent("sensor-updated", {
                  detail: {
                    id: sensorId,
                    sensorName: sensorCoords.sensorName,
                    code: resultScan,
                  },
                })
              );
            }
          })
          .catch((err) => {
            console.error(err);
            swal({
              icon: "error",
              title: t("Oops...", { defaultValue: "Oops..." }),
              text:
                err.response?.data?.message ||
                t("sensor_config_save_error", {
                  defaultValue: "Impossible d’enregistrer la configuration du capteur.",
                }),
            });
          });
      } else {
        swal({
          icon: "error",
          title: t("Error"),
          text: t("fill_required_fields", { defaultValue: "Please fill in all required fields." }),
        });
      }
    }
  };

  let niv = ["Mv1", "Mv2", "Mv3"];

  const onChangeHandlerDate = (value, name, label) => {
    setDateSetting((state) => ({ ...state, [`${name}_${label}`]: value }), []);
  };
  const onChangeHandlerMax = (value, name, label) => {
    setMaxSetting((state) => ({ ...state, [`${name}_${label}`]: value }), []);
  };
  const onChangeHandlerMin = (value, name, label) => {
    setMinSetting((state) => ({ ...state, [`${name}_${label}`]: value }), []);
  };
  const onChangeHandlerDepth = (value, name, label) => {
    setDepthSetting((state) => ({ ...state, [`${name}_${label}`]: value }), []);
  };
  useEffect(() => {
    let dataArray = [];
    dataArray.push({
      date: dateSetting,
      min: minSetting,
      max: maxSetting,
      depth: depthSetting,
    });
    setSettingValues(dataArray);
  }, [dateSetting, minSetting, maxSetting, depthSetting]);

  useEffect(() => {
    let dataArray = [];
    dataArray.push({ date: dateSetting, min: minSetting, max: maxSetting });
    setSettingValues(dataArray);
  }, [dateSetting, minSetting, maxSetting]);

  const Items = () => {
    let element = [];
    const depthValues = [mv1Depth, mv2Depth, mv3Depth];

    for (let index = 0; index < niv.length; index++) {
      element.push(
        <tbody key={index}>
          <tr>
            <td>
              <strong>{niv[index]}</strong>
            </td>
            <td>
              <input
                name={"date"}
                style={{
                  minHeight: 32,
                  marginRight: 5,
                  outline: "none",
                  border: "1px solid var(--sf-night-border, #e4e4e4)",
                }}
                type="date"
                required
                value={
                  dateSetting != null &&
                  typeof dateSetting[`Mv${index + 1}_date`] !== "undefined"
                    ? dateSetting[`Mv${index + 1}_date`]
                    : ""
                }
                onChange={(e) =>
                  onChangeHandlerDate(e.target.value, niv[index], "date")
                }
              />
            </td>
            <td>
              <input
                name={"min"}
                style={{
                  minHeight: 32,
                  marginRight: 5,
                  outline: "none",
                  border: "1px solid var(--sf-night-border, #e4e4e4)",
                }}
                value={
                  minSetting != null &&
                  typeof minSetting[`Mv${index + 1}_min`] !== "undefined"
                    ? minSetting[`Mv${index + 1}_min`]
                    : ""
                }
                onChange={(e) =>
                  onChangeHandlerMin(e.target.value, niv[index], "min")
                }
              />
            </td>
            <td>
              <input
                name={"max"}
                style={{
                  minHeight: 32,
                  marginRight: 5,
                  outline: "none",
                  border: "1px solid var(--sf-night-border, #e4e4e4)",
                }}
                value={
                  maxSetting != null &&
                  typeof maxSetting[`Mv${index + 1}_max`] !== "undefined"
                    ? maxSetting[`Mv${index + 1}_max`]
                    : ""
                }
                onChange={(e) =>
                  onChangeHandlerMax(e.target.value, niv[index], "max")
                }
              />
            </td>
            <td>
              <input
                name={"depth"}
                type="number"
                style={{
                  minHeight: 32,
                  marginRight: 5,
                  outline: "none",
                  border: "1px solid var(--sf-night-border, #e4e4e4)",
                  width: "100px",
                }}
                value={depthValues[index]}
                readOnly
                placeholder="cm"
              />
            </td>
          </tr>
        </tbody>
      );
    }
    return element;
  };
  return (
    <Container fluid className="sensor-settings-page">
      <header className="sensor-settings-header">
        <div>
          <span className="sensor-settings-eyebrow">SMARTFARM</span>
          <h1>{t("edit_sensor")}</h1>
          <p>
            {t("sensor_installation_overview", {
              defaultValue:
                "Manage sensor details, thresholds and field assignments.",
            })}
          </p>
        </div>
        <span className="sensor-settings-header-icon">
          <Settings2 size={22} aria-hidden="true" />
        </span>
      </header>

      {/* NEW: Tabs for switching views */}
      <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
        <Row>
          <Col lg="12">
            <Nav variant="tabs" className="sensor-settings-tabs mb-3">
              <Nav.Item>
                <Nav.Link eventKey="sensor">
                  <Cpu size={15} aria-hidden="true" />
                  {t("vue_capteur") || "Vue Capteur"}
                </Nav.Link>
              </Nav.Item>

              <Nav.Item>
                <Nav.Link eventKey="field">
                  <MapPinned size={15} aria-hidden="true" />
                  {t("vue_field_capteur") || "Vue Field-Capteur"}
                </Nav.Link>
              </Nav.Item>
            </Nav>
          </Col>
        </Row>

        <Tab.Content>
          {/* EXISTING: Sensor View */}
          <Tab.Pane eventKey="sensor">
            <Row>
              <Col lg="12" md="12" sm="12">
                <div className="sensor-settings-panel table-responsive">
                  <table className="sensor-settings-table table mb-0 text-center">
                    <thead className="bg-light">
                      <tr>
                        <th scope="col" className="border-0">
                          {t("sensor_Name")}
                        </th>
                        <th scope="col" className="border-0">
                          {t("sensor_code")}
                        </th>
                        <th scope="col" className="border-0">
                          {t("Actions")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sensorData.map((item, indx) => {
                        return (
                          <tr key={indx}>
                            <td>{item.sensorName || "-"}</td>
                            <td>{item.code}</td>
                            <td>
                              <ButtonGroup size="sm" className="mr-2">
                                <Button
                                  onClick={() => getSingleSensor(item.id)}
                                  variant="info"
                                  title={t("edit")}
                                  aria-label={t("edit")}
                                >
                                  <i className="fas fa-pen" aria-hidden="true" />
                                </Button>
                                <Button
                                  title={t("history")}
                                  aria-label={t("history")}
                                  onClick={() => {
                                    navigate(`/my-history/${item.code}`);
                                  }}
                                  variant="outline-primary"
                                >
                                  <i className="fas fa-clock-rotate-left" aria-hidden="true" />
                                </Button>
                              </ButtonGroup>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Col>
            </Row>

            {/* Existing sensor edit form */}
            {sensorId && (
              <Row className="py-4">
                <Col lg="12" md="8" sm="12" className="mb-4">
                  <Card small className="sensor-settings-edit-card h-100">
                    <Card.Header className="border-bottom">
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "flex-start",
                          width: "auto",
                          float: "left",
                        }}
                      >
                        <h6 className="m-0">{t("sensor_setup")}</h6>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "flex-end",
                          gap: "10px",
                          alignItems: "center",
                        }}
                      >
                        <Button
                          onClick={editSensor}
                          className="mb-2 mr-1 btn btn-success"
                        >
                          <i className="fa fa-check mx-2" aria-hidden="true"></i>
                          {t("save")}
                        </Button>
                        <Button
                          className="mb-2 mr-1 btn btn-danger"
                          onClick={() => setSensorId(null)}
                        >
                          <i className="fa fa-times mx-2" aria-hidden="true"></i>
                          {t("cancel")}
                        </Button>
                      </div>
                    </Card.Header>
                    <Card.Body className="pt-0 px-1">
                      <Row className="p-2">
                        <Col lg="8" sm="12" md="12">
                          <Form>
                            <Row>
                              <Col lg="6" md="12" sm="12">
                                <FormControl
                                  sx={{ m: 1, width: "25ch" }}
                                  variant="outlined"
                                  size="small"
                                >
                                  <InputLabel htmlFor="outlined-adornment-sensorName">
                                    {t("sensor_Name")}
                                  </InputLabel>
                                  <OutlinedInput
                                    id="outlined-adornment-sensorName"
                                    type="text"
                                    placeholder={t("sensor_Name")}
                                    value={sensorCoords.sensorName}
                                    onChange={(e) =>
                                      setSensorCoords({
                                        ...sensorCoords,
                                        sensorName: e.target.value,
                                      })
                                    }
                                    label={t("sensor_Name")}
                                  />
                                </FormControl>
                              </Col>
                              <Col lg="6" md="12" sm="12">
                                <FormControl
                                  sx={{ m: 1, width: "25ch" }}
                                  variant="outlined"
                                  size="small"
                                >
                                  <InputLabel htmlFor="outlined-adornment-password">
                                    {t("sensor_code")}
                                  </InputLabel>
                                  <OutlinedInput
                                    id="outlined-adornment-password"
                                    type="text"
                                    placeholder={t("sensor_code")}
                                    value={resultScan}
                                    endAdornment={
                                      <InputAdornment position="end">
                                        <IconButton
                                          aria-label="toggle password visibility"
                                          edge="end"
                                        >
                                          <QrCodeScannerIcon />
                                        </IconButton>
                                      </InputAdornment>
                                    }
                                    label={t("sensor_code")}
                                  />
                                </FormControl>
                              </Col>
                              <Col lg="6" md="12" sm="12">
                                <FormControl
                                  sx={{ m: 1, width: "25ch" }}
                                  variant="outlined"
                                  size="small"
                                >
                                  <InputLabel htmlFor="outlined-adornment-password">
                                    {t("Frequency")}
                                  </InputLabel>
                                  <OutlinedInput
                                    id="outlined-adornment-password"
                                    placeholder={t("Frequency")}
                                    value={frequency}
                                    readOnly={!isAdmin}
                                    onChange={(e) => {
                                      setFrequency(e.target.value);
                                    }}
                                    label={t("Frequency")}
                                  />
                                </FormControl>
                              </Col>
                              {!isAdmin && (
                                <Col lg="6" md="12" sm="12">
                                  <FormControl
                                    sx={{ m: 1, width: "25ch" }}
                                    variant="outlined"
                                    size="small"
                                  >
                                    <InputLabel id="frequency-multiplier-label">
                                      {t("Add_Frequency")}
                                    </InputLabel>
                                    <Select
                                      labelId="frequency-multiplier-label"
                                      id="frequency-multiplier"
                                      value={frequencyMultiplier}
                                      onChange={(e) => {
                                        setFrequencyMultiplier(e.target.value);
                                        if (e.target.value === "1")
                                          setFrequency("86400");
                                        else if (e.target.value === "2")
                                          setFrequency("43200");
                                        else if (e.target.value === "4")
                                          setFrequency("21600");
                                      }}
                                      label={t("Add_Frequency")}
                                    >
                                      <MenuItem value="1">
                                        {t("1 fois/jour")}
                                      </MenuItem>
                                      <MenuItem value="2">
                                        {t("2 fois/jour")}
                                      </MenuItem>
                                      <MenuItem value="4">
                                        {t("4 fois/jour")}
                                      </MenuItem>
                                    </Select>
                                  </FormControl>
                                </Col>
                              )}
                              <Col lg="6" md="12" sm="12">
                                <FormControl
                                  sx={{ m: 1, width: "25ch" }}
                                  variant="outlined"
                                  size="small"
                                >
                                  <InputLabel htmlFor="outlined-adornment-password">
                                    {t("lat")}
                                  </InputLabel>
                                  <OutlinedInput
                                    id="outlined-adornment-password"
                                    type="text"
                                    placeholder={t("lat")}
                                    value={sensorCoords.lat}
                                    onChange={(e) =>
                                      setSensorCoords({
                                        ...sensorCoords,
                                        lat: e.target.value,
                                      })
                                    }
                                    label={t("lat")}
                                  />
                                </FormControl>
                              </Col>
                              <Col lg="6" md="12" sm="12">
                                <FormControl
                                  sx={{ m: 1, width: "25ch" }}
                                  variant="outlined"
                                  size="small"
                                >
                                  <InputLabel htmlFor="outlined-adornment-password">
                                    {t("lon")}
                                  </InputLabel>
                                  <OutlinedInput
                                    id="outlined-adornment-password"
                                    type="text"
                                    placeholder={t("lon")}
                                    value={sensorCoords.lon}
                                    onChange={(e) =>
                                      setSensorCoords({
                                        ...sensorCoords,
                                        lon: e.target.value,
                                      })
                                    }
                                    label={t("lon")}
                                  />
                                </FormControl>
                              </Col>
                            </Row>
                            <Row form>
                              <Col lg="12" md="12" sm="12">
                                <div className="table-responsive">
                                  <table className="table mb-0 border text-center">
                                    <thead className="bg-light">
                                      <tr>
                                        <th
                                          scope="col"
                                          className="border-0"
                                        ></th>
                                        <th scope="col" className="border-0">
                                          {t("Date")}
                                        </th>
                                        <th scope="col" className="border-0">
                                          {t("Min")} (%)
                                        </th>
                                        <th scope="col" className="border-0">
                                          {t("Max")} (%)
                                        </th>
                                        <th scope="col" className="border-0">
                                          {t("Depth", { defaultValue: "Profondeur" })} (cm)
                                        </th>
                                      </tr>
                                    </thead>
                                    {Items()}
                                  </table>
                                </div>
                              </Col>
                            </Row>
                          </Form>
                        </Col>
                        <Col lg="4" md="12" sm="12" className="mt-1">
                          <div className="table-responsive p-2">
                            <table className="table mb-0 border text-center mt-2">
                              <thead className="bg-light">
                                <tr>
                                  <th scope="col" className="border-0">
                                    {t("Timezone")}
                                  </th>
                                  <th scope="col" className="border-0">
                                    {t("Location")}
                                  </th>
                                  <th scope="col" className="border-0">
                                    {t("Link_to_map")}
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr>
                                  <td>
                                    GMT +{sensorCoords.locationInfo.offset}
                                  </td>
                                  <td>
                                    {sensorCoords.locationInfo.timezone_id}
                                  </td>
                                  <td>
                                    <a
                                      href={sensorCoords.locationInfo.map_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      {t("view_position", { defaultValue: "View position" })}
                                    </a>
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}
          </Tab.Pane>

          {/* NEW: Field-Sensor View */}

          <Tab.Pane eventKey="field">
            <Row>
              <Col lg="12" md="12" sm="12">
                <Card small className="sensor-settings-edit-card mb-4">
                  <Card.Header className="border-bottom">
                    <h6 className="m-0">
                      {t("select_field") ||
                        "Sélectionner un champ pour voir l'historique"}
                    </h6>
                  </Card.Header>
                  <Card.Body>
                    <div className="table-responsive">
                      <table className="bg-light table mb-0 text-center table-bordered">
                        <thead className="bg-light">
                          <tr>
                            <th scope="col" className="border-0">
                              {t("field_name") || "Nom du champ"}
                            </th>
                            <th scope="col" className="border-0">
                              {t("farm_name") || "Ferme"}
                            </th>
                            <th scope="col" className="border-0">
                              {t("desc")}
                            </th>
                            <th scope="col" className="border-0">
                              {t("address") || "Address"}
                            </th>
                            <th scope="col" className="border-0">
                              {t("Actions")}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {fieldsData.length === 0 ? (
                            <tr>
                              <td colSpan="5" className="text-center py-4">
                                <i
                                  className="fas fa-folder-open text-muted"
                                  style={{ fontSize: "48px" }}
                                />
                                <p className="text-muted mb-0">
                                  {t("no_fields_found") || "Aucun champ trouvé"}
                                </p>
                              </td>
                            </tr>
                          ) : (
                            fieldsData.map((field, indx) => {
                              return (
                                <tr key={indx}>
                                  <td>
                                    <strong>{field.name || "-"}</strong>
                                  </td>
                                  <td>{field.farmName || "-"}</td>
                                  <td>{field.description || "-"}</td>
                                  <td>{field.address || "-"}</td>
                                  <td>
                                    <Button
                                      size="sm"
                                      onClick={() =>
                                        navigate(`/field-history/${field.id}`, {
                                          state: { field },
                                        })
                                      }
                                      variant="primary"
                                    >
                                      <i className="fas fa-clock-rotate-left me-1" aria-hidden="true" />{" "}
                                      {t("view_history") || "Voir l'historique"}
                                    </Button>
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>
    </Container>
  );
};

export default SensorSettings;
